import { MouseEvent, ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useEscapeKey from '../hooks/useEscapeKey'

// Touch browsers fire an emulated mouseenter right after a tap. Hover events
// this soon after a touch are ignored, so the tap's click alone decides.
const TOUCH_HOVER_GUARD_MS = 800
// Gap between trigger and bubble, and the bubble's minimum viewport inset.
const OFFSET_PX = 8

interface TooltipProps {
  /** The explanation shown in the bubble. */
  content: ReactNode
  /**
   * The term itself, rendered with a dotted underline as the trigger. Omit it
   * to render a small "?" icon instead, for use beside a control or heading.
   */
  children?: ReactNode
  /** Names the term for screen readers when the trigger is the "?" icon. */
  label?: string
  className?: string
}

/**
 * Explains a piece of jargon. Opens on hover or keyboard focus; a click or tap
 * pins it open, and a second tap, a tap outside or Escape closes it.
 *
 * The bubble is portalled to <body> with fixed positioning so cards with
 * overflow hidden cannot clip it, and is clamped inside the viewport.
 */
const Tooltip = ({ content, children, label, className = '' }: TooltipProps) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const pinned = useRef(false)
  const lastTouch = useRef(0)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const id = useId()

  const close = useCallback(() => {
    pinned.current = false
    setOpen(false)
  }, [])

  useEscapeKey(close, open)

  // Above the trigger, or below it when there is no room above.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    const bubble = bubbleRef.current
    if (!trigger || !bubble) return
    const t = trigger.getBoundingClientRect()
    const b = bubble.getBoundingClientRect()
    const above = t.top - b.height - OFFSET_PX
    const top = above >= OFFSET_PX ? above : t.bottom + OFFSET_PX
    const maxLeft = window.innerWidth - b.width - OFFSET_PX
    const left = Math.max(OFFSET_PX, Math.min(t.left + t.width / 2 - b.width / 2, maxLeft))
    setPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (open) updatePosition()
    else setPosition(null)
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return

    const onOutsidePress = (event: Event) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || bubbleRef.current?.contains(target)) return
      close()
    }

    document.addEventListener('mousedown', onOutsidePress)
    document.addEventListener('touchstart', onOutsidePress)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      document.removeEventListener('mousedown', onOutsidePress)
      document.removeEventListener('touchstart', onOutsidePress)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, close, updatePosition])

  const triggerProps = {
    ref: triggerRef,
    type: 'button' as const,
    'aria-describedby': open ? id : undefined,
    onMouseEnter: () => {
      if (Date.now() - lastTouch.current > TOUCH_HOVER_GUARD_MS) setOpen(true)
    },
    onMouseLeave: () => {
      if (!pinned.current) setOpen(false)
    },
    onFocus: () => setOpen(true),
    onBlur: close,
    onTouchStart: () => {
      lastTouch.current = Date.now()
    },
    // The trigger may sit inside a clickable card, label or form: the tap
    // belongs to the tooltip alone.
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      pinned.current = !pinned.current
      setOpen(pinned.current)
    },
  }

  return (
    <>
      {children != null ? (
        <button
          {...triggerProps}
          className={`cursor-help rounded-sm text-left underline decoration-dotted decoration-gray-400 underline-offset-2 hover:decoration-gray-600 dark:decoration-gray-500 dark:hover:decoration-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${className}`}
        >
          {children}
        </button>
      ) : (
        <button
          {...triggerProps}
          aria-label={label ? `What is ${label}?` : 'More info'}
          className={`relative inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-gray-300 align-middle text-[10px] font-semibold leading-none text-gray-500 before:absolute before:-inset-2 before:content-[''] hover:border-gray-400 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-400 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${className}`}
        >
          ?
        </button>
      )}
      {open &&
        createPortal(
          <div
            ref={bubbleRef}
            id={id}
            role="tooltip"
            className="fixed z-50 w-max rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white shadow-lg"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              maxWidth: `min(18rem, calc(100vw - ${2 * OFFSET_PX}px))`,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  )
}

export default Tooltip
